import React, { CSSProperties } from 'react'
import styled from 'styled-components'
import { textOverflowFadeEffect } from '../../components/Color/ColorDot/ColorDot.styles'
import { ReactComponent as BlendMode } from '@sketch/icons/circles-2-overlapping-diagonal-17'
import { capitalize } from '@sketch/utils'
import { Text, CopyToClipboard } from '@sketch/components'

export type AttributeDisplay = 'inline' | 'wide' | 'block'

const AttributeLabel = styled(Text.Div)`
  flex: none;
  font-weight: 400;
  display: flex;
  padding: 6px 0; /* stylelint-disable-line scales/space */
  line-height: 1.462; /* stylelint-disable-line scales/line-heights */
  color: ${({ theme }) => theme.colors.foreground.secondary.D};
  font-size: ${({ theme }) => theme.fontSizes.D};
`

export interface ValueProps {
  label?: string
  valueString?: string
}

const AttributeValue = styled.div.attrs(
  ({
    children,
    label,
    valueString,
  }: {
    children: JSX.Element | string
    label: string
    valueString: string
  }) => ({
    'aria-label':
      typeof children === 'string'
        ? `${label} ${children}`
        : `${label} ${valueString}`,
  })
)<ValueProps>`
  flex: 2;
  min-width: 0;
  font-size: ${({ theme }) => theme.fontSizes.D};
  line-height: 1.538; /* stylelint-disable-line scales/line-heights */
  color: ${({ theme }) => theme.colors.foreground.secondary.A};

  display: flex;
  align-items: center;

  padding: 4px 0;
`

const labelStyles = (display?: AttributeDisplay) => {
  switch (display) {
    case 'wide':
      return `width: 66.66%;`
    case 'block':
      return `width: 100%;`
    default:
      return `width: 96px`
  }
}

const AttributeList = styled.div`
  /* Vertical spacing between AttributeLists with the exception of Fills, this is not
  the best approach to style it but I think it's the easiest to read and mantain */
  & + &:not([data-testid='fill']) {
    margin-top: 20px;
  }
`

export interface ContainerProps {
  display?: AttributeDisplay
  isUsingCopyOnAttributeClick?: boolean
}

const Container = styled.div<ContainerProps>`
  color: ${({ theme }) => theme.colors.foreground.secondary.A};
  display: ${({ display }) =>
    display && display !== 'wide' ? display : 'flex'};
  flex-wrap: wrap;
  font-size: 0.8125rem;
  position: relative;

  ${AttributeLabel} {
    ${props => labelStyles(props.display)}
  }

  .CopyToClipboard {
    visibility: ${props =>
      props.isUsingCopyOnAttributeClick ? `hidden` : `visible`};
    width: ${({ isUsingCopyOnAttributeClick }) =>
      !isUsingCopyOnAttributeClick && '100%'};

    ${AttributeLabel} {
      ${props => labelStyles(props.display)}
    }
  }

  &.withDropdown .CopyToClipboard,
  &:hover .CopyToClipboard {
    visibility: visible;
  }

  &:hover .ColorText,
  &.withDropdown .ColorText {
    ${textOverflowFadeEffect}
    max-width: calc(180px - 48px);
  }
`

const BlendIcon = styled(BlendMode)`
  margin-right: 3px; /* stylelint-disable-line scales/space */
  opacity: 0.5;
  overflow: visible;
  padding-left: 1px; /* stylelint-disable-line scales/space */
  width: 17px;
`

const Blend = ({ blendMode }: { blendMode: string }) => (
  <>
    {' '}
    (<BlendIcon />
    {capitalize(blendMode)})
  </>
)

export interface AttributeProps {
  children?: string | React.ReactNode
  className?: string
  copyValue?: string
  display?: AttributeDisplay
  dropdown?: React.ReactElement
  dropdownVisible?: boolean
  style?: CSSProperties
  noCopy?: boolean
  showIcon?: boolean
}

const Attribute: React.FC<AttributeProps> = ({
  children = '',
  copyValue,
  display,
  dropdown,
  dropdownVisible,
  noCopy,
  showIcon,
  ...props
}) => {
  const copyPlacement = display === 'block' || dropdown ? 'top' : 'left'

  const getAttribute = () => {
    if (noCopy) {
      return children
    }

    if (!dropdown) {
      return (
        <>
          {children}
          {copyValue && (
            <CopyToClipboard
              showIcon={showIcon}
              value={copyValue}
              placement={copyPlacement}
              dropdown={dropdown}
            />
          )}
        </>
      )
    }

    if (copyValue) {
      return (
        <CopyToClipboard
          showIcon={showIcon}
          value={copyValue}
          placement="left"
          dropdown={dropdown}
          disabled={dropdownVisible}
        >
          {children}
        </CopyToClipboard>
      )
    }

    return children
  }

  return (
    <Container
      display={display}
      isUsingCopyOnAttributeClick={!dropdown}
      {...props}
      className={
        dropdownVisible ? `withDropdown ${props.className}` : props.className
      }
    >
      {getAttribute()}
    </Container>
  )
}

export { Attribute, AttributeLabel, AttributeValue, Blend, AttributeList }
